import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { HeatPlanBackupItem } from './HeatPlanBackupItem';
import { HeatPlanCategory } from './HeatPlanCategory';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class HeatPlan extends BaseObj {
    public ID: number;
    public __id: number;

    public Arm: boolean;
    public __arm: boolean;

    public BackupTimes: HeatPlanBackupItem[];
    public __backuptimes: HeatPlanBackupItem[];

    public Categories: HeatPlanCategory[];
    public __categories: HeatPlanCategory[];

    public EventID: number;
    public __eventid: number;

    public Finished: boolean;
    public __finished: boolean;

    public RaceID: number;
    public __raceid: number;

    public ScheduledStarttime: Date | null;
    public __scheduledstarttime: Date | null;

    public SortOrder: number;
    public __sortorder: number;

    public Started: boolean;
    public __started: boolean;

    public Starttime: Date | null;
    public __starttime: Date | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Arm?: boolean;
        BackupTimes?: HeatPlanBackupItem[];
        Categories?: HeatPlanCategory[];
        EntityState?: EntityState;
        EventID?: number;
        Finished?: boolean;
        RaceID?: number;
        ScheduledStarttime?: Date | null;
        SortOrder?: number;
        Started?: boolean;
        Starttime?: Date | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<HeatPlan>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('HeatPlan tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Arm = values.Arm ?? false;
        this.__arm = values.Arm ?? false;
        Object.defineProperty(this, 'Arm', {
            get(): boolean {
                return this.__arm;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Arm', this.__arm, value);
                this.__arm = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.BackupTimes = values.BackupTimes ?? [];
        this.__backuptimes = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.BackupTimes ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('BackupTimes', newVal),
        });
        this.__backuptimes.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('BackupTimes', this.BackupTimes),
            );
        });
        Object.defineProperty(this, 'BackupTimes', {
            get(): HeatPlanBackupItem[] {
                return this.__backuptimes;
            },
            set(value: HeatPlanBackupItem[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__backuptimes = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: HeatPlanBackupItem[], newVal: HeatPlanBackupItem[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('BackupTimes', newVal),
                });
                this.__backuptimes.forEach((child: HeatPlanBackupItem) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('BackupTimes', this.BackupTimes),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('BackupTimes', this.BackupTimes);
            },
            enumerable: true,
            configurable: true,
        });

        this.Categories = values.Categories ?? [];
        this.__categories = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.Categories ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('Categories', newVal),
        });
        this.__categories.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Categories', this.Categories),
            );
        });
        Object.defineProperty(this, 'Categories', {
            get(): HeatPlanCategory[] {
                return this.__categories;
            },
            set(value: HeatPlanCategory[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__categories = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: HeatPlanCategory[], newVal: HeatPlanCategory[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Categories', newVal),
                });
                this.__categories.forEach((child: HeatPlanCategory) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Categories', this.Categories),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Categories', this.Categories);
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Finished = values.Finished ?? false;
        this.__finished = values.Finished ?? false;
        Object.defineProperty(this, 'Finished', {
            get(): boolean {
                return this.__finished;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Finished', this.__finished, value);
                this.__finished = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceID = values.RaceID ?? 0;
        this.__raceid = values.RaceID ?? 0;
        Object.defineProperty(this, 'RaceID', {
            get(): number {
                return this.__raceid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceID', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ScheduledStarttime = values.ScheduledStarttime ?? null;
        this.__scheduledstarttime = values.ScheduledStarttime ?? null;
        Object.defineProperty(this, 'ScheduledStarttime', {
            get(): Date | null {
                return this.__scheduledstarttime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('ScheduledStarttime', this.__scheduledstarttime, value);
                this.__scheduledstarttime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SortOrder = values.SortOrder ?? 0;
        this.__sortorder = values.SortOrder ?? 0;
        Object.defineProperty(this, 'SortOrder', {
            get(): number {
                return this.__sortorder;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('SortOrder', this.__sortorder, value);
                this.__sortorder = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Started = values.Started ?? false;
        this.__started = values.Started ?? false;
        Object.defineProperty(this, 'Started', {
            get(): boolean {
                return this.__started;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Started', this.__started, value);
                this.__started = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Starttime = values.Starttime ?? null;
        this.__starttime = values.Starttime ?? null;
        Object.defineProperty(this, 'Starttime', {
            get(): Date | null {
                return this.__starttime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('Starttime', this.__starttime, value);
                this.__starttime = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Arm": ${JSON.stringify(this.Arm)}` +
            ',' +
            `"BackupTimes": [ ${this.BackupTimes.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"Categories": [ ${this.Categories.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Finished": ${JSON.stringify(this.Finished)}` +
            ',' +
            `"RaceID": ${JSON.stringify(this.RaceID)}` +
            ',' +
            `"ScheduledStarttime": ${this.ScheduledStarttime ? '"' + formatDateIso(this.ScheduledStarttime) + '"' : null}` +
            ',' +
            `"SortOrder": ${JSON.stringify(this.SortOrder)}` +
            ',' +
            `"Started": ${JSON.stringify(this.Started)}` +
            ',' +
            `"Starttime": ${this.Starttime ? '"' + formatDateIso(this.Starttime) + '"' : null}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of HeatPlan from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Arm?: boolean;
        BackupTimes?: HeatPlanBackupItem[];
        Categories?: HeatPlanCategory[];
        EventID?: number;
        Finished?: boolean;
        RaceID?: number;
        ScheduledStarttime?: Date | null;
        SortOrder?: number;
        Started?: boolean;
        Starttime?: Date | null;
        EntityState?: 'Added' | 'Unchanged';
    }): HeatPlan {
        return new HeatPlan(values);
    }

    public static _FromServerJson(json: any): HeatPlan {
        return new HeatPlan({
            ID: json.ID,
            Arm: json.Arm,
            BackupTimes: json.BackupTimes?.map((x: any) => HeatPlanBackupItem._FromServerJson(x)),
            Categories: json.Categories?.map((x: any) => HeatPlanCategory._FromServerJson(x)),
            EventID: json.EventID,
            Finished: json.Finished,
            RaceID: json.RaceID,
            ScheduledStarttime: json.ScheduledStarttime
                ? json.ScheduledStarttime instanceof Date
                    ? json.ScheduledStarttime
                    : new Date(json.ScheduledStarttime)
                : null,
            SortOrder: json.SortOrder,
            Started: json.Started,
            Starttime: json.Starttime ? (json.Starttime instanceof Date ? json.Starttime : new Date(json.Starttime)) : null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of HeatPlan from another instance of HeatPlan.
     * @param other The HeatPlan you want to clone
     * @returns A cloned HeatPlan
     */
    public static FromOther(other: HeatPlan): HeatPlan {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return HeatPlan._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return HeatPlan.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Arm'
            | 'BackupTimes'
            | 'Categories'
            | 'EventID'
            | 'Finished'
            | 'RaceID'
            | 'ScheduledStarttime'
            | 'SortOrder'
            | 'Started'
            | 'Starttime'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'HeatPlan';
}
